import { SnackbarPresenter } from '@daily/shared/components/Snackbar';
import { useTheme } from '@daily/shared/contexts/Theme';
import {
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';
import React, { useCallback, useMemo } from 'react';

import { BreakoutHostHelpNotification } from '/components/BreakoutRooms/BreakoutHostHelpNotification';
import { useBreakoutSessionState } from '/components/BreakoutRooms/hooks/useBreakoutSessionState';
import { JoiningBreakoutRoomScreen } from '/components/BreakoutRooms/JoiningBreakoutRoomScreen';
import { FullscreenButton } from '/components/FullscreenButton';
import { GridView } from '/components/GridView';
import { PoweredByDailyBanner } from '/components/PoweredByDailyBanner';
import { Sidebar } from '/components/Sidebar';
import { SpeakerView } from '/components/SpeakerView';
import { Topbar } from '/components/Topbar';
import {
  WaitingRoomModal,
  WaitingRoomNotification,
} from '/components/WaitingRoom';
import { useRenderBreakoutsInterstitialScreen } from '/contexts/BreakoutsProvider';
import { useOrderedParticipantIds } from '/contexts/ParticipantsProvider';
import { useWaitingRoom } from '/contexts/WaitingRoomProvider';
import { EmojiReactions } from '/features/emoji-reactions/components/EmojiReactions';
import { useCallConfig } from '/hooks/useCallConfig';
import { useJoinSound } from '/hooks/useJoinSound';
import { SIDEBAR_WIDTH, SPEAKER_PARTICIPANT_BAR_WIDTH } from '/lib/constants';
import { useSidebarView } from '/lib/state/core';
import { useShowFullscreenButton } from '/lib/state/custom';
import { useViewMode } from '/lib/state/layout';

import { AudioTracks } from './AudioTracks';
import { Modals } from './Modals';

export const Call = () => {
  const { colors, zIndex } = useTheme();
  const viewMode = useViewMode();
  const [showFullscreenButton] = useShowFullscreenButton();
  const { enableEmojiReactions, poweredByDaily } = useCallConfig();
  const { setShowModal, showModal } = useWaitingRoom();

  const localSessionId = useLocalSessionId();
  const isLocalOwner = useParticipantProperty(localSessionId, 'owner');

  const isJoiningBreakout = useRenderBreakoutsInterstitialScreen();
  const { breakoutHost } = useBreakoutSessionState();
  const orderedParticipantIds = useOrderedParticipantIds();

  const [sideBarView] = useSidebarView();
  const sideBarIsOpen = sideBarView !== null;

  const addExtraSpaceForSnackbars = sideBarIsOpen && !isJoiningBreakout;

  const handleModalClose = useCallback(
    () => setShowModal(false),
    [setShowModal]
  );

  useJoinSound();

  return useMemo(
    () => (
      <div className="call">
        {poweredByDaily && <PoweredByDailyBanner />}
        <Topbar />
        <div className="main">
          {showFullscreenButton && <FullscreenButton />}
          <AudioTracks />
          {isJoiningBreakout ? (
            <JoiningBreakoutRoomScreen />
          ) : (
            <>
              {viewMode === 'grid' ? (
                <GridView participantIds={orderedParticipantIds} />
              ) : (
                <SpeakerView />
              )}
            </>
          )}
          {enableEmojiReactions && (
            <div className="reactions">
              <EmojiReactions animation="flyUp" />
            </div>
          )}
          {((isJoiningBreakout && localSessionId === breakoutHost) ||
            !isJoiningBreakout) && <Sidebar />}
        </div>
        {isLocalOwner && (
          <>
            <WaitingRoomNotification />
            {showModal && <WaitingRoomModal onClose={handleModalClose} />}
          </>
        )}
        {breakoutHost === localSessionId && <BreakoutHostHelpNotification />}

        <SnackbarPresenter
          style={{
            right: `${
              addExtraSpaceForSnackbars ? Number(SIDEBAR_WIDTH) + 8 : 8
            }px`,
          }}
        />
        <Modals />
        <style jsx>{`
          .call {
            background: ${colors.custom.mainAreaBg};
            display: flex;
            flex-direction: column;
            height: 100vh;
            padding-bottom: 59px;
            position: relative;
            width: 100vw;
          }
          .main {
            display: flex;
            flex: auto;
            height: 100%;
            min-height: 0;
            overflow: hidden;
            position: relative;
          }
          .main :global(.fullscreen-button) {
            left: 8px;
            position: fixed;
            top: calc(var(--header-height) + 8px);
            z-index: ${zIndex.header};
          }
          .reactions {
            bottom: 0;
            position: absolute;
            right: ${(sideBarIsOpen ? Number(SIDEBAR_WIDTH) : 0) +
            (viewMode === 'speaker' ? SPEAKER_PARTICIPANT_BAR_WIDTH : 0) +
            100}px;
          }
        `}</style>
      </div>
    ),
    [
      addExtraSpaceForSnackbars,
      breakoutHost,
      colors.custom.mainAreaBg,
      enableEmojiReactions,
      handleModalClose,
      isJoiningBreakout,
      isLocalOwner,
      localSessionId,
      orderedParticipantIds,
      poweredByDaily,
      showFullscreenButton,
      showModal,
      sideBarIsOpen,
      viewMode,
      zIndex.header,
    ]
  );
};
